import {
  Calendar,
  Clock,
  Users,
  ArrowRight,
  CheckCircle,
  Zap,
  Target,
  BarChart,
  Mail,
  Phone,
  Building,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

type DemoFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  companySize: string;
  role: string;
  currentTools: string;
  useCase: string;
  preferredDate: string;
  preferredTime: string;
  goals: string;
  newsletter: boolean;
};

const createInitialFormData = (): DemoFormData => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  companySize: "",
  role: "",
  currentTools: "",
  useCase: "",
  preferredDate: "",
  preferredTime: "",
  goals: "",
  newsletter: false,
});

const getDefaultSuccessMessage = (option: "demo" | "trial") =>
  option === "demo"
    ? "Thank you for scheduling a demo with us. We'll send you a calendar invite shortly with all the details."
    : "Your free trial has been activated! Check your email for login credentials and next steps.";

const DemoPage = () => {
  const [selectedOption, setSelectedOption] = useState<"demo" | "trial">(
    "demo",
  );
  const [formData, setFormData] = useState<DemoFormData>(() => createInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>("");

  const handleInputChange = <K extends keyof DemoFormData>(
    field: K,
    value: DemoFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const payload = {
        ...formData,
        selectedOption,
        preferredDate: selectedOption === "demo" ? formData.preferredDate : "",
        preferredTime: selectedOption === "demo" ? formData.preferredTime : "",
      };

      const response = await apiClient.submitDemoRequest(payload);

      if (response.error) {
        const validationSummary = response.details
          ? Object.entries(response.details)
              .map(([field, message]) => `${field}: ${message}`)
              .join(", ")
          : response.error;
        const finalMessage = validationSummary || "Failed to submit request";
        setErrorMessage(finalMessage);
        toast.error(finalMessage);
        return;
      }

      const message = response.data?.message ?? getDefaultSuccessMessage(selectedOption);
      setFeedbackMessage(message);
      setSubmitted(true);
      toast.success(message);
      setFormData(createInitialFormData());
    } catch (error) {
      console.error("Error submitting demo request:", error);
      const fallbackMessage = "An unexpected error occurred. Please try again.";
      setErrorMessage(fallbackMessage);
      toast.error(fallbackMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Zap,
      title: "AI-Powered Automation",
      description: "See how AI can automate your marketing workflows",
    },
    {
      icon: Target,
      title: "Personalized Targeting",
      description: "Learn about our advanced audience segmentation",
    },
    {
      icon: BarChart,
      title: "Real-time Analytics",
      description: "Explore our comprehensive reporting dashboard",
    },
  ];

  const features = [
    "AI Content Generation",
    "Campaign Automation",
    "Performance Analytics",
    "Multi-channel Integration",
    "A/B Testing Tools",
    "Real-time Optimization",
    "Team Collaboration",
    "Custom Reporting",
  ];

  const companySizes = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-1000 employees",
    "1000+ employees",
  ];

  const roles = [
    "Marketing Manager",
    "Digital Marketing Specialist",
    "CMO/VP Marketing",
    "Marketing Director",
    "Growth Manager",
    "Content Manager",
    "Business Owner",
    "Other",
  ];

  const timeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
  ];

  if (submitted) {
    return (
      <Wrapper
        className="py-20 relative min-h-screen flex items-center"
        data-oid="cvbo0h."
      >
        <Container data-oid="a8tbw__">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-oid="m0_vxoh"
          >
            <div
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              data-oid="edwn7wg"
            >
              <CheckCircle
                className="w-10 h-10 text-green-600"
                data-oid="b3etqda"
              />
            </div>
            <h1
              className="text-3xl md:text-4xl font-bold mb-4"
              data-oid="_d2eoz8"
            >
              {selectedOption === "demo" ? "Demo Scheduled!" : "Trial Started!"}
            </h1>
            <p
              className="text-xl text-muted-foreground mb-8"
              data-oid="v7yk89f"
            >
              {feedbackMessage || getDefaultSuccessMessage(selectedOption)}
            </p>
            <div
              className="relative overflow-hidden border border-blue-200 rounded-2xl p-6 mb-8 bg-gradient-to-br from-blue-50 via-white to-purple-50"
              data-oid="-9.g1lx"
            >
              <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent_55%)]" />
              <div className="relative space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className="font-semibold text-lg text-slate-900" data-oid="s-m58p.">
                    What happens next?
                  </h3>
                  <Badge variant="secondary" className="w-fit bg-blue-600/10 text-blue-700 border-blue-200">
                    {selectedOption === "demo" ? "Live demo prep" : "Trial activation"}
                  </Badge>
                </div>
                <ul className="text-left space-y-3 text-sm text-slate-600" data-oid="umld4vk">
                  {selectedOption === "demo" ? (
                    [
                      "You'll receive a calendar invite with your meeting link",
                      "Solution engineers tailor the walkthrough to your stack",
                      "We highlight the workflows that matter most to your KPIs",
                      "Dedicated Q&A to outline next steps and success metrics",
                    ].map((item, idx) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1 inline-block h-2 w-2 rounded-full bg-blue-500" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))
                  ) : (
                    [
                      "Check your inbox for secure login credentials",
                      "Follow the guided onboarding to configure workspaces",
                      "Connect your marketing accounts and data sources",
                      "Launch your first AI-assisted campaign with built-in templates",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1 inline-block h-2 w-2 rounded-full bg-blue-500" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              data-oid="_sej23:"
            >
              <Link to="/" data-oid="0.k0uxu">
                <Button size="lg" className="px-8" data-oid="lxm9xd.">
                  Back to Home
                </Button>
              </Link>
              <Link to="/features" data-oid="fs779x:">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8"
                  data-oid="-a.5tq6"
                >
                  Explore Features
                </Button>
              </Link>
            </div>
          </motion.div>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper className="py-20 relative" data-oid="5lqr37p">
      {/* Hero Section */}
      <Container data-oid="s7o_1xb">
        <div className="text-center max-w-4xl mx-auto mb-16" data-oid="8:jdatt">
          <motion.h1
            className="text-4xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="m686v2z"
          >
            Experience Blyrha's
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="hhw069w"
            >
              {" "}
              AI Power
            </span>
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid="887r1_x"
          >
            See how AI can transform your marketing efforts. Schedule a
            personalized demo or start your free trial today.
          </motion.p>
        </div>
      </Container>

      {/* Demo/Trial Toggle */}
      <Container data-oid="5pk81b:">
        <motion.div
          className="max-w-2xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-oid="d8uygkm"
        >
          <div
            className="grid grid-cols-2 gap-4 p-1 bg-muted rounded-lg"
            data-oid="ft-e8ao"
          >
            <button
              onClick={() => setSelectedOption("demo")}
              className={`p-6 rounded-lg transition-all duration-200 ${
                selectedOption === "demo"
                  ? "bg-background shadow-sm border"
                  : "hover:bg-muted/50"
              }`}
              data-oid="_6c9_ty"
            >
              <Calendar
                className={`w-8 h-8 mx-auto mb-3 ${selectedOption === "demo" ? "text-blue-600" : "text-muted-foreground"}`}
                data-oid="xqwwaih"
              />
              <h3 className="font-semibold mb-2" data-oid="1v9agk3">
                Schedule Demo
              </h3>
              <p className="text-sm text-muted-foreground" data-oid="zq3tj3y">
                30-min personalized demo with our team
              </p>
            </button>
            <button
              onClick={() => setSelectedOption("trial")}
              className={`p-6 rounded-lg transition-all duration-200 ${
                selectedOption === "trial"
                  ? "bg-background shadow-sm border"
                  : "hover:bg-muted/50"
              }`}
              data-oid="q8uorsl"
            >
              <Zap
                className={`w-8 h-8 mx-auto mb-3 ${selectedOption === "trial" ? "text-blue-600" : "text-muted-foreground"}`}
                data-oid=":6rcvml"
              />
              <h3 className="font-semibold mb-2" data-oid="7m8-ysm">
                Start Free Trial
              </h3>
              <p className="text-sm text-muted-foreground" data-oid="-3xrk9o">
                14-day full access, no credit card required
              </p>
            </button>
          </div>
        </motion.div>
      </Container>

      {/* Form and Benefits */}
      <Container data-oid=":7.ee6y">
        <div className="grid lg:grid-cols-2 gap-12 mb-20" data-oid="y:3.mfd">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-oid="6hpdqh0"
          >
            <div className="bg-card border rounded-2xl p-8" data-oid="x7wntfn">
              <h2 className="text-2xl font-bold mb-6" data-oid="4djr902">
                {selectedOption === "demo"
                  ? "Schedule Your Demo"
                  : "Start Your Free Trial"}
              </h2>

              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-oid="35erulz"
              >
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4" data-oid="j8av4kw">
                  <div data-oid="nxykizz">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="82uivqv"
                    >
                      First Name *
                    </label>
                    <Input
                      required
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      placeholder="John"
                      data-oid="awv7l8m"
                    />
                  </div>
                  <div data-oid="lu-q:6j">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="4au4v08"
                    >
                      Last Name *
                    </label>
                    <Input
                      required
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      placeholder="Doe"
                      data-oid="fioj_-s"
                    />
                  </div>
                </div>

                <div data-oid="s9jdy2u">
                  <label
                    className="block text-sm font-medium mb-2"
                    data-oid="l0vx7yp"
                  >
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="john@company.com"
                    data-oid=":kr26au"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4" data-oid="_xy6ie0">
                  <div data-oid="ducv8lw">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="dv-cywv"
                    >
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      placeholder="+41 441234567"
                      data-oid="c_lcb_s"
                    />
                  </div>
                  <div data-oid="yidurg8">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid=":us6ror"
                    >
                      Company *
                    </label>
                    <Input
                      required
                      value={formData.company}
                      onChange={(e) =>
                        handleInputChange("company", e.target.value)
                      }
                      placeholder="Company Name"
                      data-oid="p55ey-x"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-oid="qzqjp5s">
                  <div data-oid="umkbezp">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="qig94hd"
                    >
                      Company Size
                    </label>
                    <Select
                      value={formData.companySize || undefined}
                      onValueChange={(value) =>
                        handleInputChange("companySize", value)
                      }
                      data-oid="pr9z_0x"
                    >
                      <SelectTrigger data-oid="409553m">
                        <SelectValue
                          placeholder="Select size"
                          data-oid="jjxe9zb"
                        />
                      </SelectTrigger>
                      <SelectContent data-oid="p1u_n88">
                        {companySizes.map((size) => (
                          <SelectItem
                            key={size}
                            value={size}
                            data-oid=":a4uba9"
                          >
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div data-oid="7vrx.vs">
                    <label
                      className="block text-sm font-medium mb-2"
                      data-oid="352ax75"
                    >
                      Your Role
                    </label>
                    <Select
                      value={formData.role || undefined}
                      onValueChange={(value) =>
                        handleInputChange("role", value)
                      }
                      data-oid="4hg8.bn"
                    >
                      <SelectTrigger data-oid="d7x9e7z">
                        <SelectValue
                          placeholder="Select role"
                          data-oid="fut449l"
                        />
                      </SelectTrigger>
                      <SelectContent data-oid="0ypqbwb">
                        {roles.map((role) => (
                          <SelectItem
                            key={role}
                            value={role}
                            data-oid="0h6qxpt"
                          >
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedOption === "demo" && (
                  <div className="grid grid-cols-2 gap-4" data-oid="t0y8eh8">
                    <div data-oid="u6qs9d9">
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="0krb-pu"
                      >
                        Preferred Date
                      </label>
                      <Input
                        type="date"
                        value={formData.preferredDate}
                        onChange={(e) =>
                          handleInputChange("preferredDate", e.target.value)
                        }
                        min={new Date().toISOString().split("T")[0]}
                        data-oid="6feiz0p"
                      />
                    </div>
                    <div data-oid="kpm22ke">
                      <label
                        className="block text-sm font-medium mb-2"
                        data-oid="pk.5usm"
                      >
                        Preferred Time
                      </label>
                      <Select
                        value={formData.preferredTime || undefined}
                        onValueChange={(value) =>
                          handleInputChange("preferredTime", value)
                        }
                        data-oid="q7uf:r8"
                      >
                        <SelectTrigger data-oid="q4hf6yg">
                          <SelectValue
                            placeholder="Select time"
                            data-oid="f_3l49b"
                          />
                        </SelectTrigger>
                        <SelectContent data-oid="0feq8ty">
                          {timeSlots.map((time) => (
                            <SelectItem
                              key={time}
                              value={time}
                              data-oid="c.t391m"
                            >
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div data-oid="xyywabz">
                  <label
                    className="block text-sm font-medium mb-2"
                    data-oid="mav6wgp"
                  >
                    {selectedOption === "demo"
                      ? "What would you like to see in the demo?"
                      : "What are your main marketing goals?"}
                  </label>
                  <Textarea
                    value={formData.goals}
                    onChange={(e) => handleInputChange("goals", e.target.value)}
                    placeholder={
                      selectedOption === "demo"
                        ? "Tell us about your specific use cases and what you'd like to focus on during the demo..."
                        : "Describe your current marketing challenges and what you hope to achieve..."
                    }
                    rows={4}
                    data-oid="o66ty3."
                  />
                </div>

                <div className="flex items-center space-x-2" data-oid="drzlfeb">
                  <Checkbox
                    id="newsletter"
                    checked={formData.newsletter}
                    onCheckedChange={(checked) =>
                      handleInputChange("newsletter", checked === true)
                    }
                    data-oid="f1vbtv:"
                  />

                  <label
                    htmlFor="newsletter"
                    className="text-sm"
                    data-oid="8em1gn-"
                  >
                    Subscribe to our newsletter for AI marketing tips and
                    updates
                  </label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                  data-oid="uy_84np"
                >
                  {isSubmitting ? (
                    <>
                      <div
                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
                        data-oid="ns7bgrx"
                      ></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {selectedOption === "demo"
                        ? "Schedule Demo"
                        : "Start Free Trial"}
                      <ArrowRight className="w-4 h-4 ml-2" data-oid="6:7j5gb" />
                    </>
                  )}
                </Button>
                {errorMessage && (
                  <p
                    className="text-sm text-red-600 text-center"
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                )}
              </form>
            </div>
          </motion.div>

          {/* Benefits & Features */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            data-oid="7c7kykz"
          >
            {/* What You'll Get */}
            <div data-oid="zfb3taj">
              <h3 className="text-2xl font-bold mb-6" data-oid="ms3sj2q">
                {selectedOption === "demo"
                  ? "What You'll See"
                  : "What You'll Get"}
              </h3>
              <div className="space-y-4" data-oid="oi9-u-e">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-4 rounded-lg border bg-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                      data-oid="-.10:w3"
                    >
                      <Icon
                        className="w-6 h-6 text-blue-600 mt-1"
                        data-oid="1vr6agm"
                      />
                      <div data-oid="09d:9vx">
                        <h4 className="font-semibold mb-1" data-oid="slxv1x.">
                          {benefit.title}
                        </h4>
                        <p
                          className="text-sm text-muted-foreground"
                          data-oid="mu86dw9"
                        >
                          {benefit.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Features Available */}
            <div data-oid="mos6umu">
              <h3 className="text-xl font-semibold mb-4" data-oid="qrrqg4d">
                Features Available
              </h3>
              <div className="grid grid-cols-2 gap-3" data-oid="6d6lxz1">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-2 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 1.1 + index * 0.05 }}
                    data-oid="op8n0:j"
                  >
                    <CheckCircle
                      className="w-4 h-4 text-green-500"
                      data-oid="ond-dl3"
                    />
                    {feature}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div
              className="p-6 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 border"
              data-oid="7jc8vyn"
            >
              <h4 className="font-semibold mb-4" data-oid="d7a86n2">
                Join Thousands of Marketers
              </h4>
              <div
                className="grid grid-cols-2 gap-4 text-center"
                data-oid=".:4p-.7"
              >
                <div data-oid="t1dv9l-">
                  <div
                    className="text-2xl font-bold text-blue-600"
                    data-oid="e6re8sm"
                  >
                    10,000+
                  </div>
                  <div
                    className="text-sm text-muted-foreground"
                    data-oid="ci81.d1"
                  >
                    Active Users
                  </div>
                </div>
                <div data-oid=".fb_xrp">
                  <div
                    className="text-2xl font-bold text-blue-600"
                    data-oid="g.n-ilr"
                  >
                    250%
                  </div>
                  <div
                    className="text-sm text-muted-foreground"
                    data-oid="84.lw7c"
                  >
                    Avg. ROI Increase
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>

      {/* Trust Indicators */}
      <Container data-oid="tb1jqi6">
        <motion.div
          className="text-center p-8 rounded-2xl border bg-card"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="rsixn5u"
        >
          <h3 className="text-xl font-semibold mb-4" data-oid="3uv3t5s">
            Trusted by Leading Companies
          </h3>
          <p className="text-muted-foreground mb-6" data-oid="w5arlv6">
            Join hundreds of companies already using Blyrha to transform their
            marketing
          </p>
          <div
            className="flex flex-wrap justify-center gap-6 opacity-60"
            data-oid="hc6uo2."
          >
            {[
              "TechCorp",
              "Innovation Labs",
              "Growth Co",
              "Marketing Pro",
              "AI Ventures",
            ].map((company, index) => (
              <div
                key={index}
                className="text-lg font-medium"
                data-oid="g5amk3_"
              >
                {company}
              </div>
            ))}
          </div>
        </motion.div>
      </Container>
    </Wrapper>
  );
};

export default DemoPage;
